import axios from "axios";

export const ORIGIN = process.env.REACT_APP_BACKEND_URL;
export const API = `${ORIGIN}/api`;
const http = axios.create({ baseURL: API });

export const token = () => localStorage.getItem("griot-token") || "";
export const setToken = (t) => (t ? localStorage.setItem("griot-token", t) : localStorage.removeItem("griot-token"));

http.interceptors.request.use((cfg) => {
  const t = token();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

http.interceptors.response.use(
  (r) => r,
  (e) => {
    const url = e?.config?.url || "";
    if (e?.response?.status === 401 && !url.includes("/auth/login")) {
      setToken("");
      if (!window.location.pathname.startsWith("/admin/login")) window.location.assign("/admin/login");
    }
    return Promise.reject(e);
  }
);

export const adminLab = () => localStorage.getItem("griot-admin-lab") || "";
export const setAdminLab = (slug) => localStorage.setItem("griot-admin-lab", slug);
const L = (params = {}) => ({ params: { ...params, ...(adminLab() ? { lab: adminLab() } : {}) } });

export const api = {
  // auth
  login: (username, password) => http.post("/auth/login", { username, password }).then((r) => r.data),
  me: () => http.get("/auth/me").then((r) => r.data),
  changePassword: (current_password, new_password) => http.post("/auth/password", { current_password, new_password }).then((r) => r.data),

  // participant
  app: () => http.get("/app").then((r) => r.data),
  labs: () => http.get("/labs").then((r) => r.data),
  resolveCode: (code) => http.post("/labs/resolve-code", { code }).then((r) => r.data),
  openings: (lab) => http.get("/openings", { params: lab ? { lab } : {} }).then((r) => r.data),
  greetings: (lab, language) => http.get("/greetings", { params: { ...(lab ? { lab } : {}), ...(language ? { language } : {}) } }).then((r) => r.data),
  greeting: (lab, index, language, who) => http.get("/greeting", { params: { ...(lab ? { lab } : {}), ...(index != null ? { index } : {}), ...(language ? { language } : {}), ...(who ? { who } : {}) } }).then((r) => r.data),
  status: (lab) => http.get("/status", { params: lab ? { lab } : {} }).then((r) => r.data),
  createSession: (opt_in, lab, code, speaker) => http.post("/sessions", { opt_in, lab, code, speaker }).then((r) => r.data),
  endSession: (sid) => http.post(`/sessions/${sid}/end`),
  turnTail: (sid) => http.get(`/sessions/${sid}/tail`).then((r) => r.data),
  sendText: (sid, text, tts, language, visitor) => http.post(`/sessions/${sid}/message`, { text, tts, language, visitor }).then((r) => r.data),
  sendVoice: (sid, blob, language, tts = true) => {
    const fd = new FormData();
    fd.append("audio", blob, "audio.webm");
    fd.append("tts", tts ? "true" : "false");
    fd.append("language", language || "auto");
    return http.post(`/sessions/${sid}/voice`, fd).then((r) => r.data);
  },

  // admin — lab-scoped
  adminApp: () => http.get("/admin/app").then((r) => r.data),
  patchApp: (body) => http.patch("/admin/app", body).then((r) => r.data),
  deleteLab: (key) => http.delete(`/admin/labs/${key}`).then((r) => r.data),
  adminLabs: () => http.get("/admin/labs").then((r) => r.data),
  createLab: (body) => http.post("/admin/labs", body).then((r) => r.data),
  patchLab: (key, body) => http.patch(`/admin/labs/${key}`, body).then((r) => r.data),
  settings: () => http.get("/admin/settings", L()).then((r) => r.data),
  patchSettings: (body) => http.patch("/admin/settings", body, L()).then((r) => r.data),
  purpose: () => http.get("/admin/purpose", L()).then((r) => r.data),
  patchPurpose: (body) => http.patch("/admin/purpose", body, L()).then((r) => r.data),
  resetPurpose: () => http.post("/admin/purpose/reset", null, L()).then((r) => r.data),
  voiceOptions: () => http.get("/admin/voice-options").then((r) => r.data),
  models: () => http.get("/admin/models").then((r) => r.data),
  probeModel: (model) => http.post("/admin/models/probe", null, { params: { model } }).then((r) => r.data),
  voicePreview: (voice, language, model, text, direction) => http.post("/admin/voice-preview", null, { params: { voice, language, model, ...(text ? { text } : {}), ...(direction ? { direction } : {}) } }).then((r) => r.data),
  voiceLab: () => http.get("/admin/voice-lab", L()).then((r) => r.data),
  voiceDesign: (description, text) => http.post("/admin/voice-design", { description, text }, L()).then((r) => r.data),
  voiceDesignKeep: (generated_voice_id, description, name) => http.post("/admin/voice-design/keep", { generated_voice_id, description, name }, L()).then((r) => r.data),
  voiceLabPreview: (voice_id, text) => http.post("/admin/voice-lab/preview", { voice_id, text }, L()).then((r) => r.data),
  voiceWake: () => http.post("/admin/voice-lab/wake", null, L()).then((r) => r.data),
  connections: () => http.get("/admin/connections").then((r) => r.data),
  addConnection: (provider, label, key) => http.post("/admin/connections", { provider, label, key }).then((r) => r.data),
  patchConnection: (id, body) => http.patch(`/admin/connections/${id}`, body).then((r) => r.data),
  deleteConnection: (id) => http.delete(`/admin/connections/${id}`).then((r) => r.data),
  reorderConnections: (provider, ids) => http.post("/admin/connections/reorder", { provider, ids }).then((r) => r.data),
  testConnection: (id) => http.post(`/admin/connections/${id}/test`).then((r) => r.data),
  testEnvConnection: (provider) => http.post("/admin/connections/test-env", { provider }).then((r) => r.data),
  wakeConnections: (provider) => http.post("/admin/connections/wake", { provider }).then((r) => r.data),
  persona: () => http.get("/admin/persona", L()).then((r) => r.data),
  publishPersona: (body) => http.post("/admin/persona/publish", body, L()).then((r) => r.data),
  rollback: (id) => http.post(`/admin/persona/${id}/rollback`, null, L()).then((r) => r.data),
  sandbox: (traits, question, brief) => http.post("/admin/persona/sandbox", { traits, question, brief }, L()).then((r) => r.data),
  promptPreview: (traits, brief) => http.post("/admin/persona/prompt-preview", { traits, brief }, L()).then((r) => r.data),
  adminOpenings: () => http.get("/admin/openings", L()).then((r) => r.data),
  getBrief: () => http.get("/admin/brief", L()).then((r) => r.data),
  digest: () => http.get("/admin/digest", L()).then((r) => r.data),
  refreshDigest: () => http.post("/admin/digest/refresh", null, L()).then((r) => r.data),
  saveBrief: (text) => http.put("/admin/brief", { text }, L()).then((r) => r.data),
  addBriefFile: (file) => { const fd = new FormData(); fd.append("file", file); return http.post("/admin/brief/files", fd, L()).then((r) => r.data); },
  delBriefFile: (id) => http.delete(`/admin/brief/files/${id}`, L()).then((r) => r.data),
  downloadBriefFile: (id) => http.get(`/admin/brief/files/${id}/download`, { ...L(), responseType: "blob" }).then((r) => r.data),
  saveOpenings: (items) => http.put("/admin/openings", { items }, L()).then((r) => r.data),
  resetOpenings: () => http.delete("/admin/openings", L()).then((r) => r.data),
  suggestWelcomes: (count, language) => http.post("/admin/greetings/suggest", null, L({ count, language })).then((r) => r.data),
  suggestOpenings: (count) => http.post("/admin/openings/suggest", null, L({ count })).then((r) => r.data),
  documents: () => http.get("/admin/documents", L()).then((r) => r.data),
  upload: (file) => { const fd = new FormData(); fd.append("file", file); return http.post("/admin/documents/upload", fd, L()).then((r) => r.data); },
  // Big media goes up in 8 MB parts so the ingress size limit never bites. A gigabyte-scale
  // upload will hit a blip somewhere, so every part gets three goes before the whole thing fails.
  uploadLarge: async (file, onProgress) => {
    const PART = 8 * 1024 * 1024;
    const { upload_id } = (await http.post("/admin/uploads/init", null, L({ name: file.name, chunk_size: PART }))).data;
    let sent = 0;
    for (let i = 0; sent < file.size; i++) {
      const slice = file.slice(sent, sent + PART);
      let last;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const fd = new FormData();
          fd.append("chunk", slice, `${file.name}.part${i}`);
          await http.post(`/admin/uploads/${upload_id}/part`, fd, L({ index: i }));
          last = null;
          break;
        } catch (e) {
          last = e;
          if (e?.response && e.response.status !== 502 && e.response.status !== 504) throw e;
          await new Promise((r) => setTimeout(r, 1200 * (attempt + 1)));
        }
      }
      if (last) throw last;
      sent += slice.size;
      onProgress?.(Math.round((sent / file.size) * 100));
    }
    return (await http.post(`/admin/uploads/${upload_id}/complete`, null, L())).data;
  },
  retry: (id) => http.post(`/admin/documents/${id}/retry`, null, L()),
  renameDoc: (id, name) => http.patch(`/admin/documents/${id}`, { name }, L()).then((r) => r.data),
  deleteDoc: (id) => http.delete(`/admin/documents/${id}`, L()),
  chunks: (id) => http.get(`/admin/documents/${id}/chunks`, L()).then((r) => r.data),
  toggleChunk: (id, excluded) => http.patch(`/admin/chunks/${id}`, null, L({ excluded })),
  metrics: () => http.get("/admin/metrics", L()).then((r) => r.data),
  resetStats: () => http.post("/admin/reset-stats", { confirm: "RESET" }, L()).then((r) => r.data),
  moderation: (params) => http.get("/admin/moderation", L(params || {})).then((r) => r.data),
};

export const errMsg = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";
